import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBolt } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { UserNamePipeModule } from '@wilson/account';
import { QualificationTrainingProviderType } from '@wilson/interfaces';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { map } from 'rxjs';
import { QualificationNamePipe } from '@wilson/qualification-v2/common';
import { TrainingQualificationsBulkEditDrawerService } from '../training-qualifications-bulk-edit-drawer.service';
import { QualificationTrainingProviderTagComponent } from '../../qualification-training-provider-tag/qualification-training-provider-tag.component';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

interface Provider {
  name: string;
  providerType: QualificationTrainingProviderType;
}

@Component({
  selector: 'wilson-training-qualifications-bulk-edit-info-section',
  imports: [
    CommonModule,
    FontAwesomeModule,
    NzTagModule,
    QualificationNamePipe,
    QualificationTrainingProviderTagComponent,
    TranslatePipe,
    UserNamePipeModule,
    NzTooltipModule,
  ],
  templateUrl:
    './training-qualifications-bulk-edit-info-section.component.html',
  styleUrl: './training-qualifications-bulk-edit-info-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationsBulkEditInfoSectionComponent {
  private readonly trainingQualificationsBulkEditDrawerService = inject(
    TrainingQualificationsBulkEditDrawerService,
  );

  protected QualificationTrainingProviderType =
    QualificationTrainingProviderType;
  protected faBolt = faBolt;

  protected usersFromTrainingsToEdit$ =
    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$.pipe(
      map((trainings) => [
        ...new Set(trainings.map((training) => training.userId)),
      ]),
    );

  protected qualificationsFromTrainingsToEdit$ =
    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$.pipe(
      map((trainings) => [
        ...new Set(
          trainings.flatMap((training) =>
            training.qualifications.map((qual) => qual.id),
          ),
        ),
      ]),
    );

  protected routeFromTrainingsToEdit$ =
    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$.pipe(
      map((trainings) => [
        ...new Set(
          trainings.flatMap((training) => {
            const routeRecord = training.routeRecord;
            if (!routeRecord) return [];

            const routePath = [
              routeRecord.startLocation?.name,
              ...(routeRecord.viaLocations?.map((location) => location.name) ||
                []),
              routeRecord.endLocation?.name,
            ].filter(Boolean);

            const routeLocalCodes = [
              routeRecord.startLocation?.locationCode,
              ...(routeRecord.viaLocations?.map(
                (location) => location.locationCode,
              ) || []),
              routeRecord.endLocation?.locationCode,
            ].filter(Boolean);

            return routePath.length > 0
              ? [
                  {
                    name: routePath.join(' - '),
                    locationCode: routeLocalCodes.join(' - '),
                  },
                ]
              : [];
          }),
        ),
      ]),
    );

  getRouteTooltipTitle(routes: { name: string; locationCode: string }): string {
    return routes.name;
  }

  protected providersFromTrainingsToEdit$ =
    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$.pipe(
      map((trainings) => {
        const uniqueProviders = new Set<string>();
        const providers: Provider[] = [];

        trainings.forEach((training) => {
          const provider = {
            name: training.organizationalUnitQualificationTrainingProvider
              .qualificationTrainingProvider.name,
            providerType:
              training.organizationalUnitQualificationTrainingProvider
                .qualificationTrainingProvider.providerType,
          };

          if (!uniqueProviders.has(provider.name)) {
            uniqueProviders.add(provider.name);
            providers.push(provider);
          }
        });

        return providers;
      }),
    );
}
